<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            class="generations-grid"
            :defaultColDef="defaultColDef"
            :columnDefs="columnDefs"
            :rowData="gridRowData"
            :getRowClass="getRowClass"
            :getRowHeight="() => 30"
            :isSearch="false"
            :master-detail="true"
            :isGroupOpenByDefault="() => true"
            :suppressRowClickSelection="true"
            :detailCellRenderer="detailCellRenderer"
            :onCellClicked="onCellClicked"
            rowSelection="multiple"
            @selection-changed="handleSelectionChange"
            grid-theme="ag-theme-alpine-owner-statement"
            :detailCellRendererParams="{
                ownersRequest: ownersRequest,
                propertiesRequest: propertiesRequest,
                updatePublishedCount: updatePublishedCount,
                updateGeneration: updateGeneration,
                handleOwnerStatementsRegenerated: handleOwnerStatementsRegenerated,
                onStatementChange: refresh,
                columnSettings: columnSettings,
                deleteGeneration: deleteGenerationFromGridRowData,
                showEmptyStatements: showEmptyStatements,
            }"
            @row-group-opened="handleRowGroupOpened"
        >
            <bulk-actions
                v-if="hasSelectedRows"
                :selected-count="currentSelectedRows.length"
                :bulk-action-defs="bulkActionDefs"
            />
            <div v-if="!hasSelectedRows" class="d-flex align-items-center justify-content-between flex-wrap">
                <div class="d-flex">
                    <responsive-button
                        :icon="['fas', 'plus']"
                        :responsiveness="false"
                        variant="primary"
                        @click="handlePlusGenerate"
                        >Generate</responsive-button
                    >
                    <responsive-button
                        :icon="['fas', 'gear']"
                        :responsiveness="false"
                        @click="$bvModal.show(settingsModalId)"
                        variant="primary"
                        >Settings</responsive-button
                    >
                </div>
                <div class="d-none d-lg-flex align-items-center pl-1">
                    <span class="filters-label">
                        <font-awesome-icon :icon="['fas', 'filter']" size="sm" class="mr-2" />
                        Filters
                    </span>
                    <div class="d-flex align-items-baseline mr-1">
                        <date-range-picker
                            class="date-range-picker"
                            :is-responsive="false"
                            :range-options="rangeOptions"
                            v-model="dateRange"
                            :date-format="userUnicodeDateFormat"
                        ></date-range-picker>
                    </div>
                    <div class="d-flex align-items-baseline mr-1">
                        <owners-search-multiselect
                            v-model="filterOwners"
                            variant="primary"
                            :owners="filterOwnerOptions"
                            itemLabel="owner"
                        />
                    </div>
                    <div class="d-flex align-items-baseline mr-1">
                        <search-multiselect
                            id="owner-statement-filter-by-properties"
                            :options="properties"
                            :icon="['fas', 'hotel']"
                            v-model="selectedProperties"
                            variant="primary"
                            itemLabel="propertie"
                            placeholder="Properties"
                            controlValue="Properties"
                        ></search-multiselect>
                    </div>
                    <div class="d-flex align-items-baseline">
                        <form-square-switch
                            class="show-empty-statements-toggle"
                            :labels="{
                                checked: 'Show Empty Statements',
                                unchecked: 'Show Empty Statements',
                            }"
                            v-model="showEmptyStatements"
                            :isToolbar="true"
                            :sync="true"
                        />
                    </div>
                </div>
                <b-button-group class="d-flex d-lg-none">
                    <more-filters-dropdown class="more-filters-dropdown" :icon="['fas', 'filter']" buttonText="Filters">
                        <template
                            v-slot:default="{
                                handleDropdownShow,
                                handleDropdownHide,
                            }"
                        >
                            <date-range-picker
                                class="date-range-picker"
                                :is-responsive="false"
                                :range-options="rangeOptions"
                                v-model="dateRange"
                                :date-format="userUnicodeDateFormat"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            ></date-range-picker>
                            <owners-search-multiselect
                                v-model="filterOwners"
                                variant="primary"
                                :owners="filterOwnerOptions"
                                itemLabel="owner"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            />
                            <search-multiselect
                                id="owner-statement-filter-by-properties"
                                :options="properties"
                                :icon="['fas', 'hotel']"
                                v-model="selectedProperties"
                                variant="primary"
                                itemLabel="propertie"
                                placeholder="Properties"
                                controlValue="Properties"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            ></search-multiselect>
                            <form-square-switch
                                class="show-empty-statements-toggle w-100"
                                :labels="{
                                    checked: 'Show Empty Statements',
                                    unchecked: 'Show Empty Statements',
                                }"
                                v-model="showEmptyStatements"
                                :isToolbar="true"
                                :sync="true"
                            />
                        </template>
                    </more-filters-dropdown>
                </b-button-group>
            </div>
            <settings-modal-form
                :modal-id="settingsModalId"
                :settings="settings"
                @update-settings="handleUpdateSettings"
            />
        </grid>
        <validation-observer ref="generate_owner_statement_form" v-slot="{ meta, setValues }">
            <modal
                :id="plusGenerateModalId"
                title="Generate Owner Statements"
                ok-title="Generate"
                @ok.prevent="generate"
                @hidden="onGenerationModalHidden"
                :scrollable="false"
                :ok-disabled="!meta.valid || loading"
            >
                <div v-if="loading" class="text-center py-5">
                    <div class="mb-4">
                        <font-awesome-icon
                            :icon="['fas', 'file-invoice-dollar']"
                            style="font-size: 3rem; color: #0d47a1;"
                            class="bounce-icon"
                        />
                    </div>
                    <h5 class="mb-3">Generating Owner Statements<span class="dots">...</span></h5>
                    <b-progress
                        :value="100"
                        height="4px"
                        animated
                        striped
                        variant="primary"
                        class="mx-auto"
                        style="max-width: 200px;"
                    ></b-progress>
                    <p class="mt-3 text-muted small">
                        Statement generation can take up to one minute, please be patient...
                    </p>
                </div>
                <div v-else>
                    <form-select
                        v-model="ownerStatementType"
                        floatingLabel="Type"
                        :options="ownerStatementTypeOptions"
                    ></form-select>
                    <form-select
                        v-if="isIndividualOwnerStatementType"
                        v-model="period"
                        floatingLabel="Period"
                        :options="periodOptions"
                    ></form-select>
                    <form-select v-model="year" floatingLabel="Year" :options="yearOptions"></form-select>
                    <form-select
                        v-if="isMonthly && isIndividualOwnerStatementType"
                        v-model="month"
                        floatingLabel="Month"
                        :options="monthOptions"
                    ></form-select>
                    <form-select
                        v-else-if="!isMonthly && isIndividualOwnerStatementType"
                        v-model="quarter"
                        floatingLabel="Quarter"
                        :options="quarterOptions"
                    ></form-select>
                    <p v-if="isSummaryOwnerStatementType">
                        Generate a report showing all revenues, fees, management commissions and owed to owner amounts.
                    </p>
                </div>
            </modal>
        </validation-observer>
    </page>
</template>

<script>
    import { format, getTime, addMonths, subYears, startOfMonth, endOfMonth, parseISO } from 'date-fns';
    import API from '../../api';
    import Page from '../generic/page/page.vue';
    import { mapGetters } from 'vuex';
    import grid from '../generic/grid/grid.vue';
    import Modal from '../generic/modal/modal.vue';
    import BusyModal from '../generic/busy-modal/busy-modal.vue';
    import BulkActions from '../generic/bulk-actions/bulk-actions.vue';
    import OwnersSearchMultiselect from '../reports/owner-statement/owners-search-multiselect.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
    import SearchMultiselect from '../generic/search-multiselect/search-multiselect.vue';
    import SearchMultiselectCard from '../generic/search-multiselect-card/search-multiselect-card.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import PropertiesSearchMultiselect from '../generic/properties-search-multiselect/properties-search-multiselect.vue';
    import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import OwnerStatementDetailCellRenderer from './owner-statement-detail-cell-renderer.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import SettingsModalForm from './settings-modal-form.vue';
    import DateRangePicker, {
        LAST_MONTH,
        LAST_WEEK,
        LAST_YEAR,
        THIS_MONTH,
        THIS_WEEK,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
        ALL_DATES,
        CUSTOM_RANGE,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import { formatResponseError } from '../../utils/api';
    import {
        REPORT_OWNER_STATEMENT_OWNERS_ENDPOINT,
        OWNER_STATEMENT_GENERATION_PROPERTY_OPTIONS_ENDPOINT,
        OWNER_STATEMENT_GENERATION_ENDPOINT,
        OWNER_STATEMENT_GENERATION_LIST_ENDPOINT,
        OWNER_STATEMENT_ENDPOINT,
        OWNER_STATEMENT_SETTINGS_ENDPOINT,
    } from '../../endpoints';

    const QUARTER_MONTHS = [
        'January, February, March',
        'April, May, June',
        'July, August, September',
        'October, November, December',
    ];

    export default {
        name: 'OwnerStatementReport',
        components: {
            grid,
            Page,
            Modal,
            BusyModal,
            FormSelect,
            FormSquareSwitch,
            BulkActions,
            DateRangePicker,
            SearchMultiselect,
            ResponsiveButton,
            ResponsiveDropdown,
            MoreFiltersDropdown,
            SearchMultiselectCard,
            OwnersSearchMultiselect,
            PropertiesSearchMultiselect,
            actionsCellRenderer: ActionsCellRenderer,
            OwnerStatementDetailCellRenderer,
            SettingsModalForm,
        },
        data() {
            return {
                busy: false,
                loading: false,
                tabs: [
                    { title: 'Generation', active: true },
                    { title: `Logs`, route: `/app/owner-statement/list/logs/`, active: false },
                ],
                currentSelectedRows: [],
                dateRequestFormat: 'yyyy-MM-dd',
                ownerStatementType: 'individual',
                period: 'monthly',
                year: null,
                month: null,
                quarter: null,
                ownerStatementTypeOptions: [
                    { text: 'Individual', value: 'individual' },
                    { text: 'Summary', value: 'summary' },
                    { text: 'IRS 1099', value: 'irs1099' },
                ],
                periodOptions: [
                    { text: 'Monthly', value: 'monthly' },
                    { text: 'Quarterly', value: 'quarterly' },
                ],
                rangeOptions: [
                    TODAY,
                    YESTERDAY,
                    THIS_WEEK,
                    LAST_WEEK,
                    THIS_MONTH,
                    LAST_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                    ALL_DATES,
                    CUSTOM_RANGE,
                ],
                gridRowData: [],
                defaultColDef: {
                    minWidth: 80,
                    sortable: false,
                },
                filterOwnerOptions: [],
                properties: [],
                filterOwnerOptionIds: new Set(),
                propertyOptionIds: new Set(),
                plusGenerateModalId: 'plus-generate-modal-id',
                detailCellRenderer: 'OwnerStatementDetailCellRenderer',
                settingsModalId: 'settings-modal',
                settings: null,
                filersLoaded: false,
            };
        },
        created() {
            this.setDefaultsForPeriod();
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol', 'userTimeFormat']),
            columnDefs() {
                return [
                    {
                        field: 'from_date',
                        headerName: 'Statements Period',
                        cellRenderer: 'agGroupCellRenderer',
                        sortable: true,
                        sort: 'desc',
                        checkboxSelection: true,
                        headerCheckboxSelection: true,
                        valueFormatter: (params) => {
                            if (params.data) {
                                return `${format(
                                    parseISO(params.data.from_date),
                                    this.userUnicodeDateFormat
                                )} - ${format(parseISO(params.data.to_date), this.userUnicodeDateFormat)}`;
                            }
                            return '';
                        },
                        maxWidth: 260,
                        minWidth: 240,
                    },
                    {
                        field: 'statements_count',
                        headerName: 'Statements',
                        sortable: true,
                        minWidth: 100,
                        maxWidth: 200,
                        cellRenderer: ({ data }) => {
                            return data.created ? data.statements_count : '';
                        },
                    },
                    {
                        headerName: '',
                        field: 'generated',
                        type: 'dateTimeColumn',
                        minWidth: 190,
                        sortable: true,
                        resizable: false,
                        cellStyle: {
                            fontStyle: 'italic',
                            fontWeight: 'normal',
                        },
                    },
                    { field: 'published_count', hide: true },
                    {
                        headerName: '',
                        colId: 'actionsColumn',
                        minWidth: 164,
                        maxWidth: 164,
                        resizable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            ownersRequest: this.filterOwners,
                            propertiesRequest: this.selectedProperties,
                            onDeleteSuccess: this.onDeleteSuccess,
                            onRegenerateSuccess: this.onRegenerateSuccess,
                            showEmptyStatements: this.showEmptyStatements,
                        },
                    },
                ];
            },
            bulkActionDefs() {
                return [
                    {
                        label: 'Generate Summary',
                        group: '',
                        onClick: this.handleGenerateSummaryAction,
                        disabled: false,
                    },
                    {
                        label: 'Generate IRS 1099',
                        group: '',
                        onClick: this.handleGenerateIRS1099Action,
                        disabled: false,
                    },
                ];
            },
            isIndividualOwnerStatementType() {
                return this.ownerStatementType === 'individual';
            },
            isSummaryOwnerStatementType() {
                return this.ownerStatementType === 'summary';
            },
            hasSelectedRows() {
                return this.currentSelectedRows.length > 0;
            },
            columnSettings() {
                return this.settings?.reservations_table_columns || [];
            },
            dateRangeLimit() {
                const minDate = startOfMonth(addMonths(subYears(new Date(), 5), 1));
                const maxDate = endOfMonth(new Date());
                return {
                    minDate,
                    maxDate,
                };
            },
            quarterOptions() {
                const { minDate, maxDate } = this.dateRangeLimit;
                const selectedYear = this.year;
                const quarters = [];
                for (let i = 0; i < 4; i++) {
                    const quarterStartMonth = i * 3;
                    const quarterStartDate = new Date(selectedYear, quarterStartMonth, 1);
                    if (
                        getTime(quarterStartDate) >= getTime(minDate) &&
                        getTime(quarterStartDate) <= getTime(maxDate)
                    ) {
                        quarters.push({ value: i, text: `Q${i + 1} (${QUARTER_MONTHS[i]})`, quarter: i });
                    }
                }

                return quarters;
            },
            yearOptions() {
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth();
                if (currentMonth === 11) {
                    return [
                        { value: currentYear, text: currentYear },
                        { value: currentYear - 1, text: currentYear - 1 },
                        { value: currentYear - 2, text: currentYear - 2 },
                        { value: currentYear - 3, text: currentYear - 3 },
                        { value: currentYear - 4, text: currentYear - 4 },
                    ];
                }
                return [
                    { value: currentYear, text: currentYear },
                    { value: currentYear - 1, text: currentYear - 1 },
                    { value: currentYear - 2, text: currentYear - 2 },
                    { value: currentYear - 3, text: currentYear - 3 },
                    { value: currentYear - 4, text: currentYear - 4 },
                    { value: currentYear - 5, text: currentYear - 5 },
                ];
            },
            monthOptions() {
                const year = this.year;
                const { minDate, maxDate } = this.dateRangeLimit;
                const fMinDate = getTime(new Date(minDate));
                const fMaxDate = getTime(new Date(maxDate));
                const options = [
                    { value: 0, text: 'January' },
                    { value: 1, text: 'February' },
                    { value: 2, text: 'March' },
                    { value: 3, text: 'April' },
                    { value: 4, text: 'May' },
                    { value: 5, text: 'June' },
                    { value: 6, text: 'July' },
                    { value: 7, text: 'August' },
                    { value: 8, text: 'September' },
                    { value: 9, text: 'October' },
                    { value: 10, text: 'November' },
                    { value: 11, text: 'December' },
                ];
                const monthOptions = [];
                options.forEach((option) => {
                    const fDate = getTime(new Date(year, option.value, 1));
                    if (fDate >= fMinDate && fDate <= fMaxDate) {
                        monthOptions.push(option);
                    }
                });
                return monthOptions;
            },
            isMonthly() {
                return this.period === 'monthly';
            },
            dateRangeRequest() {
                if (!this.isIndividualOwnerStatementType) {
                    const from_date = new Date(this.year, 0, 1);
                    const to_date = endOfMonth(new Date(this.year, 11, 1));
                    return {
                        from: format(from_date, this.dateRequestFormat),
                        to: format(to_date, this.dateRequestFormat),
                    };
                } else if (this.period === 'monthly') {
                    if (this.month === null || this.month < 0 || this.month > 11) return { from: '', to: '' };
                    const from_date = new Date(this.year, this.month, 1);
                    const to_date = endOfMonth(from_date);
                    return {
                        from: format(from_date, this.dateRequestFormat),
                        to: format(to_date, this.dateRequestFormat),
                    };
                } else if (this.period === 'quarterly') {
                    if (this.quarter === null || this.quarter < 0 || this.quarter > 3) return { from: '', to: '' };
                    const quarterStartMonth = this.quarter * 3;
                    const from_date = new Date(this.year, quarterStartMonth, 1);
                    const to_date = endOfMonth(new Date(this.year, quarterStartMonth + 2, 1));
                    return {
                        from: format(from_date, this.dateRequestFormat),
                        to: format(to_date, this.dateRequestFormat),
                    };
                }
                return { from: '', to: '' };
            },
            ownersRequest() {
                return this.filterOwners.join(',');
            },
            propertiesRequest() {
                return this.selectedProperties.join(',');
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            canGenerateReport() {
                return this.filterOwners?.length > 0;
            },
            filterOwners: {
                get() {
                    const defaultOwners = this.$store.getters['ownerStatementNewLocalStorage/owners'];
                    return defaultOwners.filter((ownerId) => this.filterOwnerOptionIds.has(ownerId));
                },
                set(owners) {
                    this.$store.dispatch('ownerStatementNewLocalStorage/changeOwners', owners);
                },
            },
            selectedProperties: {
                get() {
                    const defaultProperties = this.$store.getters['ownerStatementNewLocalStorage/properties'];
                    return defaultProperties.filter((propertyId) => this.propertyOptionIds.has(propertyId));
                },
                set(owners) {
                    this.$store.dispatch('ownerStatementNewLocalStorage/changeProperties', owners);
                },
            },
            dateRange: {
                get() {
                    const defaultDateRange = this.$store.getters['ownerStatementNewLocalStorage/dateRange'];
                    if (defaultDateRange && defaultDateRange.from && defaultDateRange.to) {
                        return {
                            ...defaultDateRange,
                            from: parseISO(defaultDateRange.from),
                            to: parseISO(defaultDateRange.to),
                        };
                    }
                    return defaultDateRange;
                },
                set(value) {
                    const dateRange = { ...value, from: value.from.toISOString(), to: value.to.toISOString() };
                    this.$store.dispatch('ownerStatementNewLocalStorage/changeDateRange', dateRange);
                },
            },
            showEmptyStatements: {
                get() {
                    return this.$store.getters['ownerStatementNewLocalStorage/showEmptyStatements'];
                },
                set(value) {
                    this.$store.dispatch('ownerStatementNewLocalStorage/changeShowEmptyStatements', value);
                },
            },
        },
        mounted() {
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            (async () => {
                this.busy = true;
                try {
                    const settingsPromise = this.fetchSettings();
                    await Promise.all([this.fetchOwners(), this.fetchProperties()]).then(() => {
                        this.filtersLoaded = true;
                    });
                    await this.fetchOwnerStatementGenerations(), await settingsPromise;
                } finally {
                    this.busy = false;
                }
            })();
        },
        methods: {
            refresh() {
                this.fetchOwnerStatementGenerations();
            },
            onCellClicked(event) {
                if (event.column.colId !== 'actionsColumn') {
                    this.onRowClicked(event);
                }
            },
            onRowClicked(event) {
                if (event.node.master) {
                    event.node.setExpanded(!event.node.expanded);
                }
            },
            handleGenerateSummary() {
                this.$router.push({
                    path: '/app/owner-statement/list/summary',
                    query: {
                        from_date: this.dateRangeRequest.from,
                        to_date: this.dateRangeRequest.to,
                        owner_ids: this.ownersRequest,
                        property_ids: this.propertiesRequest,
                    },
                });
            },
            handleGenerateSummaryAction() {
                this.$router.push({
                    path: '/app/owner-statement/list/summary',
                    query: {
                        generation_ids: this.currentSelectedRowsIds().join(','),
                        owner_ids: this.ownersRequest,
                        property_ids: this.propertiesRequest,
                    },
                });
            },
            handleGenerateIRS1099() {
                this.$router.push({
                    path: '/app/owner-statement/list/irs1099',
                    query: {
                        from_date: this.dateRangeRequest.from,
                        to_date: this.dateRangeRequest.to,
                        owner_ids: this.ownersRequest,
                        property_ids: this.propertiesRequest,
                    },
                });
            },
            handleGenerateIRS1099Action() {
                this.$router.push({
                    path: '/app/owner-statement/list/irs1099',
                    query: {
                        generation_ids: this.currentSelectedRowsIds().join(','),
                        owner_ids: this.ownersRequest,
                        property_ids: this.propertiesRequest,
                    },
                });
            },
            handleSelectionChange() {
                const gridApi = this.$refs.grid.gridOptions.api;
                this.currentSelectedRows = gridApi.getSelectedRows();
            },
            currentSelectedRowsIds() {
                return this.currentSelectedRows.map((row) => row.id);
            },
            async handleOwnerStatementsRegenerated(id) {
                await this.fetchOwnerStatements(id);
                this.successToast({ text: 'All selected owner statements regenerated successfully.' });
            },
            setDefaultsForPeriod(newPeriod = this.period) {
                const currentDate = new Date();
                const currentMonth = currentDate.getMonth();
                const currentYear = currentDate.getFullYear();
                if (newPeriod === 'monthly') {
                    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
                    const previousMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
                    this.year = previousMonthYear;
                    this.month = previousMonth;
                } else if (newPeriod === 'quarterly') {
                    const previousQuarterIndex = Math.floor((currentMonth - 1) / 3);
                    const previousQuarterYear = previousQuarterIndex < 0 ? currentYear - 1 : currentYear;
                    const adjustedQuarter = previousQuarterIndex < 0 ? 3 : previousQuarterIndex;
                    this.year = previousQuarterYear;
                    this.quarter = adjustedQuarter;
                }
            },
            validateSelections() {
                if (this.period === 'monthly') {
                    const validMonthOptions = this.monthOptions.map((option) => option.value);
                    if (!validMonthOptions.includes(this.month)) {
                        this.month = validMonthOptions[0];
                    }
                } else if (this.period === 'quarterly') {
                    const validQuarterOptions = this.quarterOptions.map((option) => option.value);
                    if (!validQuarterOptions.includes(this.quarter)) {
                        this.quarter = validQuarterOptions[0];
                    }
                }
            },
            handleRowGroupOpened(event) {
                if (event.node.expanded && event.data.children.length == 0) {
                    this.fetchOwnerStatements(event.data.id);
                }
            },
            onDeleteSuccess(statementPostfix, id) {
                this.successToast({ text: `Owner Statement${statementPostfix} successfully deleted.` });
                this.deleteGenerationFromGridRowData(id);
            },
            onRegenerateSuccess(statementPostfix, data) {
                this.successToast({ text: `Owner Statement${statementPostfix} successfully regenerated.` });
                this.updateGeneration(data.generation, data.generation.statements_count);
                this.updateGridRowData(data.statements);
            },
            async fetchOwners() {
                try {
                    const { data } = await API.get(REPORT_OWNER_STATEMENT_OWNERS_ENDPOINT());
                    this.filterOwnerOptions = data;
                    this.filterOwnerOptionIds = new Set(data.map((option) => option.id));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch owners.' });
                }
            },
            async fetchSettings() {
                try {
                    const { data } = await API.get(OWNER_STATEMENT_SETTINGS_ENDPOINT());
                    this.settings = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch settings.' });
                }
            },
            async fetchProperties() {
                try {
                    const { data } = await API.get(OWNER_STATEMENT_GENERATION_PROPERTY_OPTIONS_ENDPOINT());
                    this.properties = data.map((item) => ({ value: item.id, text: item.name }));
                    this.propertyOptionIds = new Set(data.map((option) => option.id));
                } catch (e) {
                    this.errorToast({ text: 'Could not load properties.' });
                }
            },
            async fetchOwnerStatements(generationId) {
                try {
                    const { data } = await API.get(OWNER_STATEMENT_ENDPOINT(), {
                        params: {
                            generation_id: generationId,
                            owners: this.ownersRequest,
                            properties: this.propertiesRequest,
                            show_empty_statements: this.showEmptyStatements,
                        },
                    });
                    this.updateGridRowData(data);
                } catch (e) {
                    this.errorToast({ text: 'Could not load owner statements.' });
                }
            },
            setChildrenFetched(generationId, fetched) {
                this.gridApi.forEachNode((node) => {
                    if (node.data.id === generationId) {
                        node.data.is_children_fetched = fetched;
                        this.gridApi.applyTransaction({ update: [node.data] });
                    }
                });
            },
            updateGridRowData(ownerStatements) {
                if (!ownerStatements || ownerStatements.length === 0) return;
                const generationId = ownerStatements[0].generation_id;
                this.gridApi.forEachNode((node) => {
                    if (node.data.id === generationId) {
                        node.data.children = [...ownerStatements];
                        this.gridApi.applyTransaction({ update: [node.data] });
                    }
                });
                this.setChildrenFetched(generationId, true);
            },
            updateGeneration(generation, statementsCount) {
                this.gridApi.forEachNode((node) => {
                    if (node.data.id === generation.id) {
                        node.setDataValue('generated', generation.generated);
                        node.setDataValue('statements_count', statementsCount);
                    }
                });
            },
            updatePublishedCount(id, amount) {
                this.gridApi.forEachNode((node) => {
                    if (node.data.id === id) {
                        node.setDataValue('published_count', node.data.published_count + amount);
                    }
                });
            },
            deleteGenerationFromGridRowData(id) {
                this.gridApi.forEachNode((node) => {
                    if (id === node.data.id) this.gridApi.applyTransaction({ remove: [node.data] });
                });
            },
            transformDataForAgGrid(data) {
                if (!data || !Array.isArray(data) || data.length === 0) return [];
                return data.map((generation) => {
                    return {
                        id: generation.id,
                        created: generation.created,
                        generated: generation.generated,
                        from_date: generation.from_date,
                        to_date: generation.to_date,
                        statements_count: generation.statements_count,
                        published_count: generation.published_count,
                        is_children_fetched: generation.statements_count === 0,
                        children: [],
                    };
                });
            },
            async fetchOwnerStatementGenerations() {
                try {
                    this.busy = true;
                    const { data } = await API.get(OWNER_STATEMENT_GENERATION_LIST_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            owners: this.ownersRequest,
                            properties: this.propertiesRequest,
                            show_empty_statements: this.showEmptyStatements,
                        },
                    });
                    this.gridRowData = this.transformDataForAgGrid(data);
                } catch (e) {
                    this.errorToast({ text: 'Could not load owner statement generations.' });
                } finally {
                    this.busy = false;
                }
            },
            generate() {
                if (this.isIndividualOwnerStatementType) this.generateOwnerStatements();
                if (this.ownerStatementType === 'summary') this.handleGenerateSummary();
                if (this.ownerStatementType === 'irs1099') this.handleGenerateIRS1099();
            },
            async generateOwnerStatements() {
                try {
                    this.loading = true;
                    await API.post(OWNER_STATEMENT_GENERATION_ENDPOINT(), {
                        from_date: this.dateRangeRequest.from,
                        to_date: this.dateRangeRequest.to,
                    });
                    this.refresh();
                    this.successToast({ text: 'Generation successfully created.' });
                    this.$bvModal.hide(this.plusGenerateModalId);
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not generate owner statements.' });
                } finally {
                    this.loading = false;
                }
            },
            handlePlusGenerate() {
                this.$bvModal.show(this.plusGenerateModalId);
            },
            getRowClass({ data }) {
                if (data.created) {
                    return 'lx-row-group';
                }
                return '';
            },
            handleUpdateSettings(data) {
                this.settings = data;
            },
            onGenerationModalHidden() {
                this.loading = false;
            },
        },
        watch: {
            period(newPeriod) {
                this.setDefaultsForPeriod(newPeriod);
            },
            year() {
                this.validateSelections();
            },
            dateRange() {
                this.refresh();
            },
            filterOwners() {
                if (this.filtersLoaded) {
                    this.refresh();
                }
            },
            selectedProperties() {
                if (this.filtersLoaded) {
                    this.refresh();
                }
            },
            showEmptyStatements() {
                if (this.filtersLoaded) {
                    this.refresh();
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    @import '../../scss/loading-animations.scss';

    .button-toolbar > :deep(*) {
        display: flex;
        flex-wrap: wrap-reverse !important;
        justify-content: space-between !important;
        margin-bottom: 5px;
    }

    .more-filters-dropdown {
        :deep(.buttons-wrapper) {
            width: 100%;
        }
    }

    .actions-item {
        text-align: left !important;
        width: 210px !important;
    }

    .date-range-picker {
        :deep(.dropdown) {
            margin-right: 0 !important;
        }
    }

    .filters-label {
        color: #0d47a1;
        font-weight: 600;
        padding: 0.375rem 0 0.375rem 0.75rem;
        margin: 0 1rem 0 0;
        display: inline-flex;
        align-items: center;
        border-left: 2px solid #e9ecef;

        .fa-filter {
            font-size: 0.8125rem;
            margin-right: 0.5rem;
        }
    }

    .show-empty-statements-toggle {
        width: 200px;
    }

    .generations-grid {
        :deep(.ag-row) {
            overflow: visible !important;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
        }
    }
</style>
